import React from 'react';

import HowItWorksVideo from '~/components/static/how-it-works/how-it-works-video';
import { ReviewsElement } from '~/components/static/reviews/reviews-elements';
import Tooltip from '~/components/tooltip/tooltip';
import { PlanSavings } from '~/components/plans/bundles-page/plan-bundle';
import { seasonalCopy } from '~/components/plans/bundles-page/plan-bundle-page';
import { IS_SEASON_TRUNCATED_FLAG } from '~/feature-flags';
import { lawnSubscriptionStatus } from '~/features/lawns/lawn';
import { useModal } from '~/features/modals/modal-actions';
import { useSession } from '~/features/session/session-actions';
import { centsToCurrency } from '~/utils/numbers';

import styles from '~/components/plans/bundles-page/plan-bundle-page.module.scss';

export const PlanDescription = ({
  imageSrc,
  imageAlt,
  badgeText,
  planTitle,
  planDescription,
}) => {
  return (
    <div className={styles.planBundleMainContent}>
      <div className={styles.imageContainer}>
        <img src={imageSrc} alt={imageAlt} />
        {badgeText && <div className={styles.preorderBadge}>{badgeText}</div>}
      </div>
      <div
        className={styles.mainContentTextContainer}
        id="plan-title-container-top"
      >
        <p>{planTitle}</p>
        <div className={styles.purchaseCtaSubtext}>{planDescription}</div>
      </div>
    </div>
  );
};

export const PurchaseCtaPrice = ({
  isFlexPay,
  numOfShipments,
  selectedBundle,
  showHardmarkPricing,
  showSoilTestDiscount,
}) => {
  const { user } = useSession();

  const msrpPrice = selectedBundle?.carts[0].msrp;

  const subtotal = isFlexPay
    ? selectedBundle?.carts[0].subtotal
    : selectedBundle?.fullSeasonPrice;

  // discountedPrice is the subtotal + the discount applied
  // if there is not discount, discountedPrice is the same as subtotal
  // fullSeasonDiscountedPrice is the fullSeasonPrice + the discount applied
  // if there is not discount, fullSeasonDiscountedPrice is the same as fullSeasonPrice
  const actualPrice = isFlexPay
    ? selectedBundle?.carts[0].discountedPrice
    : selectedBundle?.fullSeasonDiscountedPrice;
  const priceSuffix = ' today';

  const showSoilTestDiscountPill =
    showSoilTestDiscount &&
    user?.lawnSubscriptionStatus !== lawnSubscriptionStatus.CANCELLED &&
    subtotal > actualPrice;

  return isFlexPay ? (
    IS_SEASON_TRUNCATED_FLAG ? (
      <>
        {showSoilTestDiscountPill ? (
          <PlanSavings
            retailPrice={subtotal}
            actualPrice={actualPrice}
            title="20% off with SOILTEST coupon"
          />
        ) : showHardmarkPricing && msrpPrice > actualPrice ? (
          <PlanSavings retailPrice={msrpPrice} actualPrice={actualPrice} />
        ) : null}
        <span>{centsToCurrency(actualPrice)}</span>
        {numOfShipments > 1 && (
          <span className={styles.ctaPriceAccentText}>/ today</span>
        )}
      </>
    ) : (
      <>
        {showSoilTestDiscountPill ? (
          <PlanSavings
            retailPrice={subtotal}
            actualPrice={actualPrice}
            title="20% off with SOILTEST coupon"
            className={styles.footer}
          />
        ) : showHardmarkPricing && msrpPrice > actualPrice ? (
          <PlanSavings
            retailPrice={msrpPrice}
            actualPrice={actualPrice}
            className={styles.footer}
          />
        ) : null}
        <span>{centsToCurrency(actualPrice)}</span>
        <span className={styles.ctaPriceAccentText}>{priceSuffix}</span>
        <Tooltip
          className={styles.ctaTooltip}
          content={
            <div>
              Pay as you go with several shipments this year containing only
              what your lawn needs, at exactly the right time.
            </div>
          }
        />
      </>
    )
  ) : (
    <>
      {showSoilTestDiscountPill && (
        <PlanSavings
          retailPrice={subtotal}
          actualPrice={actualPrice}
          title="20% off with SOILTEST coupon"
          className={styles.footer}
        />
      )}
      <span>
        {centsToCurrency(showSoilTestDiscountPill ? actualPrice : subtotal)}
      </span>
      <span className={styles.ctaPriceAccentText}>{seasonalCopy}</span>
    </>
  );
};

export function HowItWorksSection({ showVideo }) {
  const { showModal } = useModal();

  const bodyCopy = showVideo
    ? `Grow your lushest lawn with a pesticide-free plan customized to deliver exactly what your lawn needs—all while safely nurturing long-term yard health.`
    : `We're excited for you, and your lawn, to enjoy the ease and benefits of Sunday's science backed lawn care.`;

  return (
    <section className={styles.whySundaySection}>
      <div className={styles.leftColumn}>
        <h2 className={styles.whySundayTitle}>
          Easiest lawn care <br />
          on planet earth
        </h2>
        <p className={styles.whySundaySubtitle}>{bodyCopy}</p>
        <div className={styles.reviews}>
          <img
            className={styles.guaranteeIcon}
            src="/icons/sunday-guarantee-badge-yellow.svg"
            alt="sunday-guarantee-badge-yellow"
          />
          <div>
            <div className={styles.howItWorksReviewsContainer}>
              <ReviewsElement linkAnchor="#reviews-section" variant="dark" />
            </div>
            <p className={styles.guaranteeText}>
              <button
                className={styles.guaranteeButton}
                onClick={() => showModal('GUARANTEE')}
              >
                Guaranteed
              </button>{' '}
              results. <br className={styles.mobileOnly} />
              Cancel anytime.
            </p>
          </div>
        </div>
      </div>
      <div className={styles.rightColumn}>
        <div className={styles.videoContainer}>
          <HowItWorksVideo className={styles.bundlesVideo} />
        </div>
      </div>
    </section>
  );
}

export const BackToTopPill = () => (
  <button className={styles.toTopPill} onClick={() => window.scrollTo(0, 0)}>
    <p>Back to top</p>
    <img
      src="/icons/up-chevron-white.svg"
      alt="White arrow pointing up"
      width="8"
      height="4"
    />
  </button>
);
